/**
 * Dashboard home page — overview of boxes, items, and recent activity.
 */

import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useBoxStore } from '../store/BoxContext';
import type { ActivityLogEntry, PaginatedResult } from '@shared/types';
import { supabase } from '../auth/supabase';

export function DashboardPage() {
  const navigate = useNavigate();
  const { state } = useBoxStore();
  const [activity, setActivity] = useState<ActivityLogEntry[]>([]);
  const [loading, setLoading] = useState(true);

  const totalBoxes = state.boxes.length;
  const totalItems = 0; // Items not loaded when listing boxes

  useEffect(() => {
    async function fetchActivity() {
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        const token = session?.access_token;

        const response = await fetch('/api/v1/activity?pageSize=5', {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });

        if (response.ok) {
          const result: PaginatedResult<ActivityLogEntry> = await response.json();
          setActivity(result.data);
        }
      } catch (error) {
        console.error('Failed to fetch activity:', error);
      } finally {
        setLoading(false);
      }
    }

    fetchActivity();
  }, []);

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-text-primary">Dashboard</h1>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 gap-4">
        <div className="rounded-lg border border-border bg-surface p-4">
          <p className="text-sm text-text-secondary">Total Boxes</p>
          <p className="text-3xl font-bold text-text-primary mt-1">{totalBoxes}</p>
        </div>
        <div className="rounded-lg border border-border bg-surface p-4">
          <p className="text-sm text-text-secondary">Total Items</p>
          <p className="text-3xl font-bold text-text-primary mt-1">{totalItems}</p>
        </div>
      </div>

      {/* Quick Actions */}
      <div>
        <h2 className="text-sm font-semibold text-text-secondary uppercase tracking-wider mb-3">
          Quick Actions
        </h2>
        <div className="grid grid-cols-1 gap-3">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-3 p-4 rounded-lg border border-border bg-surface hover:bg-accent/5 transition-colors text-left"
          >
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center">
              <svg
                className="h-5 w-5 text-accent"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M3 8a2 2 0 012-2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8z"
                />
              </svg>
            </div>
            <div>
              <p className="font-medium text-text-primary">Scan Marker</p>
              <p className="text-sm text-text-secondary">Find a box with your camera</p>
            </div>
          </button>

          <button
            onClick={() => navigate('/search')}
            className="flex items-center gap-3 p-4 rounded-lg border border-border bg-surface hover:bg-accent/5 transition-colors text-left"
          >
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center">
              <svg
                className="h-5 w-5 text-accent"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
            <div>
              <p className="font-medium text-text-primary">Search</p>
              <p className="text-sm text-text-secondary">Find boxes and items</p>
            </div>
          </button>

          <Link
            to="/boxes"
            className="flex items-center gap-3 p-4 rounded-lg border border-border bg-surface hover:bg-accent/5 transition-colors text-left"
          >
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center">
              <svg
                className="h-5 w-5 text-accent"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                />
              </svg>
            </div>
            <div>
              <p className="font-medium text-text-primary">View All Boxes</p>
              <p className="text-sm text-text-secondary">Browse your collection</p>
            </div>
          </Link>
        </div>
      </div>

      {/* Recent Activity */}
      <div>
        <h2 className="text-sm font-semibold text-text-secondary uppercase tracking-wider mb-3">
          Recent Activity
        </h2>
        {loading ? (
          <div className="text-center py-8 text-text-secondary text-sm">Loading...</div>
        ) : activity.length === 0 ? (
          <div className="text-center py-8 text-text-secondary text-sm">No recent activity</div>
        ) : (
          <div className="space-y-2">
            {activity.map((entry) => (
              <div
                key={entry.id}
                className="flex items-start gap-3 p-3 rounded-lg border border-border bg-surface"
              >
                <div className="flex-1">
                  <p className="text-sm text-text-primary">{entry.action}</p>
                  <p className="text-xs text-text-secondary mt-0.5">
                    {new Date(entry.createdAt).toLocaleString()}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
